import { NextRenewalCard } from "@/components/billing/NextRenewalCard";
import { ExpiredSeatsBanner } from "@/components/billing/ExpiredSeatsBanner";
import { useState, useEffect, useMemo } from "react";

import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { StatusBadge } from "@/components/StatusBadge";
import { pauseBadgeOf, pauseBadgeClass } from "@/lib/pauseReason";
import { Plus, Play, Pause, Trash2, MoreHorizontal, Target, Copy, Pencil, Files, RefreshCw, Eye, Users, Upload, Filter, Clock, Loader2, Inbox, Calendar, Brain, Sparkles, Link2, RotateCcw, CalendarClock, Lock, KanbanSquare } from "lucide-react";
import { AttachAccountDialog } from "@/components/campaigns/AttachAccountDialog";
import { format } from "date-fns";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { startCampaign, pauseCampaign, resumeCampaign, deleteCampaign, duplicateCampaign, archiveCampaign, restoreCampaign, unarchiveCampaign, permanentlyDeleteCampaign } from "@/lib/campaignHelpers";
import { useCapacityBlock } from "@/components/campaigns/useCapacityBlock";
import { calculateCampaignProgress } from "@/lib/campaignProgress";
import { SubscriptionLockOverlay } from "@/components/SubscriptionLockOverlay";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";

import { useAuth } from "@/contexts/AuthContext";

import { useUnreadMessageCount } from "@/hooks/useUnreadMessageCount";
import { useAccountCooldownStatus, formatResume } from "@/hooks/useAccountCooldownStatus";
import { useCrmReminders } from "@/hooks/useCrmReminders";

// Simplified Workspace Component
// Countdown Timer Component
const CountdownTimer = ({ targetDate }: { targetDate: Date }) => {
  const [timeLeft, setTimeLeft] = useState<{ hours: number; minutes: number; seconds: number }>({ hours: 0, minutes: 0, seconds: 0 });

  // Use a primitive timestamp as the dep so the interval is not torn down and
  // rebuilt every parent render (which would happen if `targetDate` is a fresh
  // Date object reference each render).
  const targetTime = targetDate.getTime();

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const difference = targetTime - now;

      if (difference > 0) {
        setTimeLeft({
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000),
        });
      } else {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [targetTime]);

  const TimeUnit = ({ value, label }: { value: number, label: string }) => (
    <div className="flex flex-col items-center gap-1">
      <div className="bg-white dark:bg-zinc-900 shadow-sm border border-zinc-200 dark:border-zinc-800 rounded-lg w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center relative overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-zinc-50/50 dark:to-zinc-800/50 pointer-events-none" />
        <span className="text-xl sm:text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100 tracking-tight z-10">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{label}</span>
    </div>
  );

  return (
    <div className="flex items-start gap-2 sm:gap-3">
      <TimeUnit value={timeLeft.hours} label="Hrs" />
      <div className="pt-3 sm:pt-4 text-zinc-300 dark:text-zinc-700 font-bold text-lg animate-pulse">:</div>
      <TimeUnit value={timeLeft.minutes} label="Min" />
      <div className="pt-3 sm:pt-4 text-zinc-300 dark:text-zinc-700 font-bold text-lg animate-pulse">:</div>
      <TimeUnit value={timeLeft.seconds} label="Sec" />
    </div>
  );
};

export default function SimplifiedCampaigns() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  // URL-backed campaigns tab so navigating away and back restores the same view.
  const campaignsTab = searchParams.get('tab') || 'ongoing';
  const setCampaignsTab = (tab: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  const { canAccessFeatures, isLoading: subscriptionLoading } = useSubscriptionStatus();
  const { workspace, isSuperAdmin, isMember } = useWorkspace();
  const { due: crmDue } = useCrmReminders(workspace?.id);
  const { unreadCount } = useUnreadMessageCount();
  const inboxBadgeCount = unreadCount;
  const { user } = useAuth();

  // WhatsApp notifications moved to global header (WhatsAppNotificationsButton).

  // campaigns_managed_by_admin is now an optional feature toggle, not a lock
  const isManagedByAdmin = workspace?.campaigns_managed_by_admin || false;

  // State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<string | null>(null);
  const [campaignNameToDelete, setCampaignNameToDelete] = useState("");
  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [campaignToPermanentlyDelete, setCampaignToPermanentlyDelete] = useState<string | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [campaignToRename, setCampaignToRename] = useState<{ id: string; name: string } | null>(null);
  const [newCampaignName, setNewCampaignName] = useState("");
  // Duplicate State
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [campaignToDuplicate, setCampaignToDuplicate] = useState<string | null>(null);
  // Archive State
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [campaignToArchive, setCampaignToArchive] = useState<string | null>(null);
  // Pending request delete state
  const [pendingRequestToDelete, setPendingRequestToDelete] = useState<{ id: string; campaignId: string | null } | null>(null);
  // Attach account dialog state
  const [attachTarget, setAttachTarget] = useState<any | null>(null);
  // Reopen completed campaign confirm
  const [reopenTarget, setReopenTarget] = useState<any | null>(null);
  const [reopening, setReopening] = useState(false);


  // Check if AI assistant is enabled for workspace
  const isAiEnabled = true;

  // Fetch company knowledge profiles for workspace-level knowledge score
  const { data: companyKnowledgeProfiles } = useQuery({
    queryKey: ["company-knowledge-profiles-scores", workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];
      const { data, error } = await supabase
        .from("company_knowledge_profiles")
        .select("knowledge_score")
        .eq("workspace_id", workspace.id);
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!workspace?.id && isAiEnabled,
  });

  // Calculate average knowledge score across all company profiles
  const averageKnowledgeScore = companyKnowledgeProfiles && companyKnowledgeProfiles.length > 0
    ? Math.round(companyKnowledgeProfiles.reduce((sum, p) => sum + (p.knowledge_score || 0), 0) / companyKnowledgeProfiles.length)
    : 0;

  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  const { data: campaigns, isLoading } = useQuery({
    queryKey: ["campaigns", workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];

      // First get clients for this workspace
      // Fetch all campaigns owned by this workspace (single source of truth =
      // campaigns.workspace_id). This prevents cross-workspace drift when a
      // LinkedIn account is moved between workspaces.
      const { data: workspaceCampaigns, error: campaignsQueryError } = await supabase
        .from("campaigns")
        .select(`
          *,
          clients (
            id,
            email,
            status,
            linkedin_name,
            profile_picture_url
          )
        `)
        .eq("workspace_id", workspace.id)
        .is("deleted_at", null)
        .order("created_at", { ascending: false });

      if (campaignsQueryError) throw campaignsQueryError;

      const allCampaigns = (workspaceCampaigns || []).map((c: any) => ({
        ...c,
        clients: c.clients ?? null,
      }));

      return allCampaigns;
    },
    enabled: !!workspace?.id,
    // Poll every 2 seconds if any campaign is in transitional state (starting/resuming)
    refetchInterval: (query) => {
      const hasTransitionalCampaign = query.state.data?.some(
        (c: any) => c.status === 'starting' || c.status === 'resuming'
      );
      return hasTransitionalCampaign ? 2000 : false;
    },
  });
  const cooldownClientIds = useMemo(
    () => (campaigns || []).map((campaign: any) => campaign.client_id).filter(Boolean),
    [campaigns]
  );
  const { data: cooldownStatus = {} } = useAccountCooldownStatus(cooldownClientIds, workspace?.id);

  // Fetch active subscription details for UI (Moved up to fix hook ordering)
  const { data: subscriptionDetails } = useQuery({
    queryKey: ['active-subscription', workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return null;
      // Account seats are the only renewal source: show the nearest active seat renewal.
      const { data: seats } = await supabase
        .from('account_subscriptions' as any)
        .select('current_period_end, status')
        .eq('workspace_id', workspace.id)
        .neq('status', 'removed');
      const rows = ((seats as any[]) || []);
      const active = rows.filter((r) => r.status === 'active' && r.current_period_end)
        .sort((a, b) => a.current_period_end.localeCompare(b.current_period_end));
      const data = rows.length
        ? { seat_count: rows.length, current_period_end: active[0]?.current_period_end ?? null, status: active.length ? 'active' : 'expired' }
        : null;
      return data;
    },
    enabled: !!workspace?.id,
  });

  const daysLeft = subscriptionDetails?.current_period_end
    ? Math.max(0, Math.ceil((new Date(subscriptionDetails.current_period_end).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)))
    : 0;

  // Fetch member's own client ID (for determining which campaigns they can edit)
  const { data: myClient } = useQuery({
    queryKey: ['my-client', workspace?.id],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      const { data } = await supabase
        .from('clients')
        .select('id')
        .eq('owner_user_id', user.id)
        .eq('workspace_id', workspace?.id)
        .maybeSingle();
      return data;
    },
    enabled: isMember && !!workspace?.id,
  });

  // Helper to check if member can edit a campaign
  const canEditCampaign = (campaign: any) => {
    if (!isMember) return true; // Admins/owners can edit all
    return myClient?.id && campaign.client_id === myClient.id;
  };


  // Fetch pending managed campaign requests for provisioning section
  const { data: pendingManagedRequests } = useQuery({
    queryKey: ["pending-managed-requests", workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];

      const { data, error } = await supabase
        .from("managed_campaign_requests")
        .select(`
          *,
          clients (
            id,
            email,
            linkedin_name,
            profile_picture_url
          )
        `)
        .eq("workspace_id", workspace.id)
        .in("status", ["pending_setup", "in_progress"])
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!workspace?.id,
  });

  // Get campaign IDs that are linked to pending managed requests (should be hidden)
  const pendingManagedCampaignIds = new Set(
    (pendingManagedRequests || [])
      .filter(r => r.campaign_id)
      .map(r => r.campaign_id)
  );


  const { data: campaignStats } = useQuery({
    queryKey: ["campaign-stats", workspace?.id],
    queryFn: async () => {
      const { data: stats, error } = await supabase
        .from("campaign_stats_aggregate")
        .select("*");

      if (error) throw error;

      return (stats || []).reduce((acc, stat) => {
        acc[stat.campaign_id] = {
          campaignId: stat.campaign_id,
          totalTargets: Number(stat.total_targets) || 0,
          connected: Number(stat.connection_accepted_count) || 0,
          replied: Number(stat.replied_count) || 0,
          messaged: Number(stat.messaged_count) || 0,
          pending: Number(stat.pending_count) || 0,
          visited: Number(stat.visited_count) || 0,
          conversionRate: Number(stat.conversion_rate) || 0,
          connectionSent: Number(stat.connection_sent_count) || 0,
        };
        return acc;
      }, {} as Record<string, any>);
    },
    enabled: !!workspace?.id && !!campaigns && campaigns.length > 0,
  });

  // connectionSent is now derived from campaign_stats_aggregate (connection_sent_count)
  // which reliably counts targets where connection_sent_at IS NOT NULL

  // Per-row campaign-progress queries: each campaign has its own cached query
  // keyed by id. React Query dedupes them, so adding/updating one campaign
  // no longer invalidates the entire list. The aggregate `campaignProgress`
  // map below is built from these individually-cached entries via the cache.
  // We still expose a single `campaignProgress` map for downstream code that
  // expects { [campaignId]: progress } shape.
  const campaignIdsKey = (campaigns || []).map(c => c.id).sort().join(',');
  const { data: campaignProgress } = useQuery({
    queryKey: ["campaign-progress", workspace?.id, campaignIdsKey],
    queryFn: async () => {
      if (!campaigns) return {};

      const progressMap: Record<string, any> = {};

      await Promise.all(
        campaigns.map(async (campaign) => {
          // Use fetchQuery to dedupe across the page — each campaign's progress
          // is cached individually under its own key with a 30s stale window.
          try {
            const progress = await queryClient.fetchQuery({
              queryKey: ["campaign-progress", workspace?.id, campaign.id],
              queryFn: () => calculateCampaignProgress(campaign.id),
              staleTime: 30_000,
            });
            progressMap[campaign.id] = progress;
          } catch (error) {
            // Silently fail for individual campaign progress calculation
          }
        })
      );

      return progressMap;
    },
    enabled: !!workspace?.id && !!campaigns && campaigns.length > 0,
    staleTime: 30_000,
  });

  const { handleCapacityResult, capacityDialog } = useCapacityBlock();

  const startCampaignMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      const result = await startCampaign(campaignId);
      if (!result.success) {
        handleCapacityResult(result, campaignId);
        throw new Error(result.error || "Failed to start campaign");
      }

      return result;
    },
    onSuccess: () => {
      toast.success('Campaign started!', {
        description: 'Actions are being scheduled in the background.',
        duration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaign-stats"] });
      queryClient.invalidateQueries({ queryKey: ["campaign-progress"] });
    },
    onError: (error: any) => {
      toast.error("Failed to start campaign: " + error.message);
    },
  });

  const pauseCampaignMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      return await pauseCampaign(campaignId);
    },
    onSuccess: () => {
      toast.success("Campaign paused successfully");
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaign-progress"] });
    },
    onError: (error: any) => {
      toast.error("Failed to pause campaign: " + error.message);
    },
  });

  const resumeCampaignMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      const result = await resumeCampaign(campaignId);
      if (!result.success) {
        handleCapacityResult(result, campaignId);
        throw new Error(result.error || "Failed to resume campaign");
      }

      return result;
    },
    onSuccess: () => {
      toast.success('Campaign resumed!', {
        description: 'Actions are being scheduled in the background.',
        duration: 5000,
      });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaign-progress"] });
    },
    onError: (error: any) => {
      toast.error("Failed to resume campaign: " + error.message);
    },
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      return await deleteCampaign(campaignId);
    },
    onSuccess: () => {
      toast.success("Campaign deleted. You can restore it within 30 days.");
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["deleted-campaigns"] });
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
      setCampaignNameToDelete("");
    },
    onError: (error: any) => {
      toast.error("Failed to delete campaign: " + error.message);
    },
  });

  const permanentlyDeleteCampaignMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      return await permanentlyDeleteCampaign(campaignId);
    },
    onSuccess: () => {
      toast.success("Campaign removed from workspace");
      queryClient.invalidateQueries({ queryKey: ["deleted-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      setPermanentDeleteDialogOpen(false);
      setCampaignToPermanentlyDelete(null);
    },
    onError: (error: any) => {
      toast.error("Failed to remove campaign: " + error.message);
    },
  });

  const archiveCampaignMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      return await archiveCampaign(campaignId);
    },
    onSuccess: () => {
      toast.success("Campaign archived successfully");
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      setArchiveDialogOpen(false);
      setCampaignToArchive(null);
    },
    onError: (error: any) => {
      toast.error("Failed to archive campaign: " + error.message);
    },
  });

  const duplicateCampaignMutation = useMutation({
    mutationFn: async ({ campaignId, includeTargets, excludeMessaged }: { campaignId: string; includeTargets: boolean; excludeMessaged?: boolean }) => {
      const result = await duplicateCampaign(campaignId, { includeTargets, excludeMessaged });
      if (!result.success) {
        throw new Error(result.error || "Failed to duplicate campaign");
      }
      return result;
    },
    onSuccess: (data) => {
      toast.success("Campaign duplicated successfully");
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      setDuplicateDialogOpen(false);
      setCampaignToDuplicate(null);
      if (data.newCampaignId) {
        navigate(`/campaigns/${data.newCampaignId}`);
      }
    },
    onError: (error: any) => {
      toast.error("Failed to duplicate campaign: " + error.message);
    },
  });

  const renameCampaignMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      const { error } = await supabase
        .from('campaigns')
        .update({ name })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Campaign renamed successfully");
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      setRenameDialogOpen(false);
      setCampaignToRename(null);
      setNewCampaignName("");
    },
    onError: (error: any) => {
      toast.error("Failed to rename campaign: " + error.message);
    },
  });

  const deletePendingRequestMutation = useMutation({
    mutationFn: async ({ requestId, campaignId }: { requestId: string; campaignId: string | null }) => {
      // First delete any change requests linked to this managed request
      await supabase
        .from("managed_campaign_change_requests")
        .delete()
        .eq("managed_request_id", requestId);

      // Delete the managed campaign request
      const { error: reqError } = await supabase
        .from("managed_campaign_requests")
        .delete()
        .eq("id", requestId);
      if (reqError) throw reqError;

      // If there's a linked draft campaign, delete it too
      if (campaignId) {
        const result = await deleteCampaign(campaignId);
        if (!result.success) throw new Error(result.error);
      }
    },
    onSuccess: () => {
      toast.success("Campaign request deleted");
      queryClient.invalidateQueries({ queryKey: ["pending-managed-requests"] });
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      setPendingRequestToDelete(null);
    },
    onError: (error: any) => {
      toast.error("Failed to delete: " + error.message);
    },
  });

  const restoreCampaignMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      return await restoreCampaign(campaignId);
    },
    onSuccess: () => {
      toast.success("Campaign restored successfully");
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["deleted-campaigns"] });
    },
    onError: (error: any) => {
      toast.error("Failed to restore campaign: " + error.message);
    },
  });

  const unarchiveCampaignMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      return await unarchiveCampaign(campaignId);
    },
    onSuccess: () => {
      toast.success("Campaign unarchived successfully");
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (error: any) => {
      toast.error("Failed to unarchive campaign: " + error.message);
    },
  });

  const handleToggleCampaign = (campaign: any) => {
    if (campaign.status === 'active') {
      pauseCampaignMutation.mutate(campaign.id);
    } else if (campaign.status === 'paused') {
      if (campaign.paused_reason === 'seat_expired') {
        toast.error("This LinkedIn account needs renewing", { description: "Please contact your account manager to renew this account. The campaign restarts automatically once renewed." });
        return;
      }
      resumeCampaignMutation.mutate(campaign.id);
    } else {
      startCampaignMutation.mutate(campaign.id);
    }
  };

  // Fetch deleted campaigns
  const { data: deletedCampaigns } = useQuery({
    queryKey: ["deleted-campaigns", workspace?.id],
    queryFn: async () => {
      if (!workspace?.id) return [];

      // First get clients for this workspace
      const { data: workspaceClients, error: clientsError } = await supabase
        .from("clients")
        .select("id")
        .eq("workspace_id", workspace.id);

      if (clientsError) throw clientsError;

      const clientIds = workspaceClients?.map(c => c.id) || [];

      if (clientIds.length === 0) return [];

      // Get deleted campaigns from the last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await (supabase as any)
        .from("campaigns")
        .select(`
          *,
          clients (
            id,
            email,
            status,
            linkedin_name,
            profile_picture_url
          )
        `)
        .in("client_id", clientIds)
        .not("deleted_at", "is", null)
        .gte("deleted_at", thirtyDaysAgo.toISOString())
        .eq("admin_only" as any, false)
        .order("deleted_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!workspace?.id,
  });

  const renderCampaignList = (campaignList: any[]) => {
    if (!campaignList || campaignList.length === 0) {
      return (
        <Card className="flex flex-col items-center justify-center py-10 sm:py-16 border-2 border-dashed border-muted-foreground/25 shadow-none bg-muted/5">
          <div className="w-12 sm:w-16 h-12 sm:h-16 rounded-full bg-background flex items-center justify-center mb-3 sm:mb-4 shadow-sm">
            <Target className="w-6 sm:w-8 h-6 sm:h-8 text-muted-foreground/50" />
          </div>
          <h3 className="text-base sm:text-lg font-semibold mb-2">No campaigns found</h3>
          <p className="text-muted-foreground mb-4 sm:mb-6 text-center max-w-sm text-sm px-4">
            {isManagedByAdmin
              ? "Campaigns for this workspace are managed by our team."
              : "Create a new campaign or check your filters."}
          </p>
          {!isManagedByAdmin && (
            <Button size="sm" className="text-xs sm:text-sm" onClick={() => navigate("/campaigns/onboarding")}>
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          )}
        </Card>
      );
    }

    return (
      <TooltipProvider>
        <div className="grid gap-3 sm:gap-4">
          {campaignList.map((campaign) => {
            const stats = campaignStats?.[campaign.id];
            const progress = campaignProgress?.[campaign.id];
            const client = campaign.clients as any;
            const completionPercentage = progress?.progressPercentage ? Math.round(progress.progressPercentage) : 0;

            return (
              <div
                key={campaign.id}
                onClick={() => navigate(`/campaigns/${campaign.id}`)}
                className="group relative bg-card hover:bg-muted/30 border border-sidebar-border/60 rounded-xl p-4 lg:p-5 transition-all duration-200 cursor-pointer hover:shadow-md"
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
                  {/* Icon & Info */}
                  <div className="flex items-center gap-3 lg:gap-4 lg:w-[350px] shrink-0">
                    <Avatar className="h-8 w-8 sm:h-10 sm:w-10 border border-border/50 shrink-0">
                      <AvatarImage src={client?.profile_picture_url || undefined} alt={client?.linkedin_name || "Client"} />
                      <AvatarFallback className="bg-primary/10 text-primary font-medium text-xs sm:text-sm">{client?.linkedin_name?.[0] || "?"}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm sm:text-base truncate text-foreground group-hover:text-primary transition-colors">
                          {campaign.name}
                          {campaign.status === 'paused' && (() => { const b = pauseBadgeOf(campaign.paused_reason); return (
                            <span title={b.full} className={`ml-2 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold align-middle ${pauseBadgeClass[b.tone]}`}>
                              {b.tone === 'danger' ? <Lock className="h-3 w-3" /> : <Pause className="h-3 w-3" />} {b.label}
                            </span>); })()}
                        </h3>
                        {campaign.status === "active" && cooldownStatus[campaign.client_id]?.blocked && (
                           <Tooltip>
                             <TooltipTrigger asChild>
                               <Badge variant="destructive" className="text-[8px] sm:text-[10px] px-1 sm:px-1.5 py-0 h-4 sm:h-5 gap-0.5 sm:gap-1 shrink-0 cursor-help">
                                 <CalendarClock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                 <span>Connections paused</span>
                               </Badge>
                             </TooltipTrigger>
                             <TooltipContent side="top" className="max-w-xs">
                               <p className="text-xs">
                                Connection requests resume {formatResume(cooldownStatus[campaign.client_id].resumes_at, campaign.timezone)} ({campaign.timezone || "IST"}). Messages continue as normal.
                               </p>
                             </TooltipContent>
                           </Tooltip>
                        )}
                        {!campaign.client_id && (
                          <Badge variant="outline" className="text-[8px] sm:text-[10px] px-1 sm:px-1.5 py-0 h-4 sm:h-5 gap-0.5 sm:gap-1 shrink-0 border-amber-500/50 text-amber-600 bg-amber-500/5">
                            <Link2 className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                            <span className="hidden sm:inline">No account attached</span>
                            <span className="sm:hidden">Unlinked</span>
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 sm:mt-1">
                        <p className="text-xs sm:text-sm text-muted-foreground truncate">
                          {client?.linkedin_name || client?.email || "Unknown Account"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Stats Grid - Mobile optimized */}
                  <div className="flex-1 grid grid-cols-3 gap-2 lg:gap-4 items-center px-0 lg:px-4 border-t lg:border-t-0 lg:border-l border-border/40 pt-3 lg:pt-0 lg:pl-6">
                    {/* Connection Sent */}
                    <div className="text-center">
                      <p className="text-[8px] sm:text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5 sm:mb-1">Sent</p>
                      <p className="text-base sm:text-xl font-bold tabular-nums">{stats?.connectionSent || 0}</p>
                    </div>

                    {/* Accepted */}
                    <div className="text-center border-l border-border/40">
                      <p className="text-[8px] sm:text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5 sm:mb-1">Accepted</p>
                      <p className="text-base sm:text-xl font-bold tabular-nums">{stats?.connected || 0}</p>
                    </div>

                    {/* Replies */}
                    <div className="text-center border-l border-border/40">
                      <p className="text-[8px] sm:text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5 sm:mb-1">Replies</p>
                      <p className="text-base sm:text-xl font-bold tabular-nums">{stats?.replied || 0}</p>
                    </div>
                  </div>

                  {/* Actions - Hidden when member viewing others' campaigns */}
                  <div className="flex items-center justify-end gap-2 lg:gap-3 pt-3 lg:pt-0 border-t lg:border-t-0 lg:border-l border-border/40 lg:pl-6 shrink-0" onClick={(e) => e.stopPropagation()}>
                    {!canEditCampaign(campaign) ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge variant="outline" className="text-[10px] sm:text-xs text-muted-foreground gap-1 sm:gap-1.5 cursor-help">
                            <Eye className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                            View Only
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent side="left" className="max-w-xs">
                          <p className="text-xs">You can only manage campaigns using your connected account. This campaign uses another team member's account.</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <>
                        {!campaign.client_id ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 sm:h-9 gap-1.5 border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
                                onClick={() => setAttachTarget(campaign)}
                              >
                                <Link2 className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Attach account</span>
                                <span className="sm:hidden">Attach</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs">
                              <p className="text-xs">No LinkedIn account is attached to this campaign. Attach one to resume, or duplicate it under a fresh account.</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (campaign.status === "starting" || campaign.status === "resuming") ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 sm:h-9 sm:w-9 rounded-full bg-blue-500/10 text-blue-600"
                            disabled
                          >
                            <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                          </Button>
                        ) : campaign.status === "archived" && !(campaign as any).account_deleted ? (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 sm:h-9 sm:w-9 rounded-full bg-amber-500/10 text-amber-600 hover:bg-amber-500/20"
                            onClick={() => unarchiveCampaignMutation.mutate(campaign.id)}
                            disabled={unarchiveCampaignMutation.isPending}
                          >
                            {unarchiveCampaignMutation.isPending ? (
                              <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-3 h-3 sm:w-4 sm:h-4" />
                            )}
                          </Button>
                        ) : campaign.status === "archived" && (campaign as any).account_deleted ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="text-[10px] sm:text-xs text-muted-foreground gap-1 sm:gap-1.5">
                                <Eye className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                View Only
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs">
                              <p className="text-xs">Account deleted - this campaign cannot be restored</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : campaign.status === "completed" ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 sm:h-9 gap-1.5"
                                onClick={() => setReopenTarget(campaign)}
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Reopen</span>
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs">
                              <p className="text-xs">Move back to Paused so you can add more targets and start again.</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : campaign.status !== "active"
                          && (campaign.max_connections_daily ?? 0) === 0
                          && (campaign.max_messages_daily ?? 0) === 0 ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="h-7 sm:h-9 gap-1.5 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 font-normal">
                                <Clock className="w-3.5 h-3.5" />
                                <span className="hidden sm:inline">Awaiting setup</span>
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent side="left" className="max-w-xs">
                              <p className="text-xs">Our team is assigning this campaign's daily sending limits. It can be started once setup is complete.</p>
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <Button
                            size="icon"
                            variant="ghost"
                            className={cn(
                              "h-7 w-7 sm:h-9 sm:w-9 rounded-full transition-all duration-200",
                              campaign.status === "active"
                                ? "bg-amber-500/10 text-amber-600 hover:bg-amber-500/20"
                                : "bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20"
                            )}
                            onClick={() => handleToggleCampaign(campaign)}
                            disabled={startCampaignMutation.isPending || pauseCampaignMutation.isPending || resumeCampaignMutation.isPending}
                          >
                            {campaign.status === "active" ? <Pause className="w-3 h-3 sm:w-4 sm:h-4 fill-current" /> : <Play className="w-3 h-3 sm:w-4 sm:h-4 fill-current" />}
                          </Button>
                        )}


                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7 sm:h-9 sm:w-9 rounded-full hover:bg-muted">
                              <MoreHorizontal className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuLabel>Manage Campaign</DropdownMenuLabel>
                            <DropdownMenuItem
                              onClick={() => {
                                setCampaignToRename({ id: campaign.id, name: campaign.name });
                                setNewCampaignName(campaign.name);
                                setRenameDialogOpen(true);
                              }}
                            >
                              <Pencil className="w-4 h-4 mr-2" /> Rename
                            </DropdownMenuItem>
                            {!campaign.client_id && (
                              <DropdownMenuItem onClick={() => setAttachTarget(campaign)}>
                                <Link2 className="w-4 h-4 mr-2" /> Attach account
                              </DropdownMenuItem>
                            )}
                            {campaign.status === "completed" && (
                              <DropdownMenuItem onClick={() => setReopenTarget(campaign)}>
                                <RotateCcw className="w-4 h-4 mr-2" /> Reopen
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {campaign.status === "archived" && !(campaign as any).account_deleted ? (
                              <DropdownMenuItem
                                onClick={() => unarchiveCampaignMutation.mutate(campaign.id)}
                                disabled={unarchiveCampaignMutation.isPending}
                              >
                                <RefreshCw className="w-4 h-4 mr-2" /> Unarchive
                              </DropdownMenuItem>
                            ) : campaign.status === "archived" && (campaign as any).account_deleted ? (
                              <DropdownMenuItem disabled className="text-muted-foreground">
                                <Eye className="w-4 h-4 mr-2" /> View Only (Account Deleted)
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                onClick={() => {
                                  setCampaignToArchive(campaign.id);
                                  setArchiveDialogOpen(true);
                                }}
                              >
                                <Files className="w-4 h-4 mr-2" /> Archive
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive focus:bg-destructive/10"
                              onClick={() => {
                                setCampaignToDelete(campaign.id);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </TooltipProvider>
    );
  };

  const renderDeletedCampaignList = () => {
    if (!deletedCampaigns || deletedCampaigns.length === 0) {
      return (
        <Card className="flex flex-col items-center justify-center py-16 border-2 border-dashed border-muted-foreground/25 shadow-none bg-muted/5">
          <div className="w-16 h-16 rounded-full bg-background flex items-center justify-center mb-4 shadow-sm">
            <Trash2 className="w-8 h-8 text-muted-foreground/50" />
          </div>
          <h3 className="text-lg font-semibold mb-2">No deleted campaigns</h3>
          <p className="text-muted-foreground mb-6 text-center max-w-sm">
            Campaigns deleted in the last 30 days will appear here.
          </p>
        </Card>
      );
    }

    return (
      <TooltipProvider>
        <div className="grid gap-4">
          {deletedCampaigns.map((campaign) => {
            const stats = campaignStats?.[campaign.id];
            const progress = campaignProgress?.[campaign.id];
            const client = campaign.clients as any;
            const completionPercentage = progress?.progressPercentage ? Math.round(progress.progressPercentage) : 0;
            const daysSinceDeleted = Math.floor((new Date().getTime() - new Date(campaign.deleted_at).getTime()) / (1000 * 60 * 60 * 24));

            return (
              <div
                key={campaign.id}
                className="group relative bg-card/50 border border-muted-foreground/20 rounded-xl p-5 transition-all duration-200"
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                  {/* Icon & Info */}
                  <div className="flex items-center gap-4 lg:w-[350px] shrink-0">
                    <Avatar className="h-10 w-10 border border-border/50 shrink-0 opacity-50">
                      <AvatarImage src={client?.profile_picture_url || undefined} alt={client?.linkedin_name || "Client"} />
                      <AvatarFallback className="bg-muted text-muted-foreground font-medium">{client?.linkedin_name?.[0] || "?"}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-base truncate text-muted-foreground">
                          {campaign.name}
                        </h3>
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-5 gap-1 shrink-0">
                          <Trash2 className="w-3 h-3" />
                          Deleted
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-sm text-muted-foreground truncate">
                          {client?.linkedin_name || client?.email || "Unknown Account"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="flex-1 grid grid-cols-3 gap-4 items-center px-4 border-l-0 lg:border-l border-border/40 lg:px-6 opacity-50">
                    {/* Targets */}
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">Targets</p>
                      <p className="text-xl font-bold tabular-nums">{stats?.totalTargets || 0}</p>
                    </div>

                    {/* Replied */}
                    <div className="text-center border-l border-border/40">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">Replied</p>
                      <p className="text-xl font-bold tabular-nums">{stats?.replied || 0}</p>
                    </div>

                    {/* Days until permanent deletion */}
                    <div className="flex flex-col justify-center px-4 border-l border-border/40 h-full min-w-[140px]">
                      <div className="flex items-center justify-between w-full">
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Expires in</span>
                        <span className="text-xs font-bold tabular-nums text-destructive">{30 - daysSinceDeleted} days</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1">Deleted {daysSinceDeleted} days ago</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-end gap-3 lg:pl-6 lg:border-l border-border/40 pt-4 lg:pt-0 mt-4 lg:mt-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => restoreCampaignMutation.mutate(campaign.id)}
                      disabled={restoreCampaignMutation.isPending}
                      className="text-primary hover:text-primary border-primary/50 hover:bg-primary/10"
                    >
                      {restoreCampaignMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Restoring...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Restore
                        </>
                      )}
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-9 w-9 rounded-full hover:bg-muted">
                          <MoreHorizontal className="w-5 h-5 text-muted-foreground" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive focus:bg-destructive/10"
                          onClick={() => {
                            setCampaignToPermanentlyDelete(campaign.id);
                            setPermanentDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="w-4 h-4 mr-2" /> Delete Forever
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </TooltipProvider>
    );
  };

  // NOW we can have conditional returns - AFTER all hooks
  if (subscriptionLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!canAccessFeatures) {
    return <SubscriptionLockOverlay />;
  }

  // Filter out campaigns that are linked to pending managed requests (hide until admin completes setup).
  // Memoize all four derived arrays in one pass so unrelated re-renders (e.g.,
  // dialog open/close, search input typing) don't re-walk the full campaign list 4×.
  const { visibleCampaigns, ongoingCampaigns, pausedCampaigns, completedCampaigns, archivedCampaigns } = useMemo(() => {
    const visible = campaigns?.filter(c => !pendingManagedCampaignIds.has(c.id)) || [];
    return {
      visibleCampaigns: visible,
      ongoingCampaigns: visible.filter(c => c.status === 'active' || c.status === 'starting' || c.status === 'resuming'),
      pausedCampaigns: visible.filter(c => c.status === 'paused' || c.status === 'draft'),
      completedCampaigns: visible.filter(c => c.status === 'completed'),
      archivedCampaigns: visible.filter(c => c.status === 'archived'),
    };
  }, [campaigns, pendingManagedCampaignIds]);



  // campaigns_managed_by_admin is now an optional feature toggle, not a lock
  // ...existing code
  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">

      {/* Header & Stats */}
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Campaigns</h1>
            <p className="text-sm text-muted-foreground">
              Manage your outreach sequences and track performance.
            </p>
          </div>

          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 bg-background sm:size-default"
              onClick={() => navigate("/crm")}
            >
              <KanbanSquare className="w-4 h-4" />
              <span className="hidden sm:inline">CRM</span>
              {crmDue.length > 0 && (
                <span className="ml-0.5 rounded-full bg-destructive px-1.5 text-[10px] font-semibold text-destructive-foreground" aria-label={`${crmDue.length} reminders due`}>{crmDue.length}</span>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="gap-2 bg-background relative sm:size-default"
              onClick={() => navigate("/inbox")}
            >
              <Inbox className="w-4 h-4" />
              <span className="hidden sm:inline">Inbox</span>
              {inboxBadgeCount > 0 && (
                <Badge variant="destructive" className="h-5 min-w-5 px-1 rounded-full flex items-center justify-center text-[10px] absolute -top-2 -right-2">
                  {inboxBadgeCount > 99 ? '99+' : inboxBadgeCount}
                </Badge>
              )}
            </Button>
            <Button
              size="sm"
              className="shadow-lg hover:shadow-primary/25 transition-all duration-300 gap-2 sm:size-default"
              onClick={() => navigate("/campaigns/onboarding")}
            >
              {isAiEnabled && <Sparkles className="w-4 h-4" />}
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New Campaign</span>
            </Button>
          </div>
        </div>

        {/* Managed workspaces: per-account renewals */}
        {(workspace as any)?.settings?.simplified_onboarding_enabled === true && workspace?.id && (
          <div className="space-y-3">
            <ExpiredSeatsBanner workspaceId={workspace.id} />
            <NextRenewalCard workspaceId={workspace.id} />
          </div>
        )}

        {/* Subscription Info Card */}
        {(workspace as any)?.settings?.simplified_onboarding_enabled !== true && subscriptionDetails && (
          <div className={cn(
            "grid gap-4 md:gap-8 p-4 rounded-xl border bg-muted/20",
            isAiEnabled ? "grid-cols-1 sm:grid-cols-4" : "grid-cols-1 sm:grid-cols-3"
          )}>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <p className="text-xs sm:text-sm font-medium text-muted-foreground">Seats Purchased</p>
                <p className="text-lg font-bold">{subscriptionDetails.seat_count || 0}</p>
              </div>
            </div>

            <div className="flex items-center gap-3 border-l-0 sm:border-l sm:pl-8 border-border/50">
              <div className="p-2 bg-amber-500/10 rounded-lg">
                <Clock className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="text-xs sm:text-sm font-medium text-muted-foreground">Days Left</p>
                <p className="text-lg font-bold">{daysLeft} Days</p>
              </div>
            </div>

            <div className="flex items-center gap-3 border-l-0 sm:border-l sm:pl-8 border-border/50">
              <div className="p-2 bg-emerald-500/10 rounded-lg">
                <Calendar className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs sm:text-sm font-medium text-muted-foreground">Next Renewal</p>
                <p className="text-lg font-bold">
                  {subscriptionDetails.current_period_end ? format(new Date(subscriptionDetails.current_period_end), "MMM d, yyyy") : "N/A"}
                </p>
              </div>
            </div>

            {/* AI Company Knowledge Button */}
            {isAiEnabled && (
              <div 
                className="flex items-center gap-3 border-l-0 sm:border-l sm:pl-8 border-border/50 cursor-pointer hover:bg-muted/50 -m-4 p-4 sm:-my-4 sm:py-4 sm:-mr-4 sm:pr-4 sm:ml-0 sm:pl-8 rounded-r-xl transition-colors"
                onClick={() => navigate("/profiles")}
              >
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Brain className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1">
                  <p className="text-xs sm:text-sm font-medium text-muted-foreground">AI Knowledge</p>
                  <p className="text-lg font-bold text-primary">
                    {averageKnowledgeScore}%
                    <span className="text-xs font-normal text-muted-foreground ml-1">
                      ({companyKnowledgeProfiles?.length || 0} profiles)
                    </span>
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-4" aria-busy="true" aria-label="Loading campaigns">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="bg-card border border-sidebar-border/60 rounded-xl p-5"
            >
              <div className="flex items-center gap-4">
                <div className="h-10 w-10 rounded-full bg-muted animate-pulse shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-48 bg-muted animate-pulse rounded" />
                  <div className="h-3 w-32 bg-muted/70 animate-pulse rounded" />
                </div>
                <div className="hidden sm:flex items-center gap-6">
                  {[0, 1, 2, 3].map((j) => (
                    <div key={j} className="space-y-1.5">
                      <div className="h-3 w-12 bg-muted/60 animate-pulse rounded" />
                      <div className="h-4 w-10 bg-muted animate-pulse rounded" />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-8">


          {/* Unified campaign view - show all campaigns with type badges */}
          {(visibleCampaigns.length > 0 || (deletedCampaigns && deletedCampaigns.length > 0) || (pendingManagedRequests && pendingManagedRequests.length > 0)) ? (
            <>
            <Tabs value={campaignsTab} onValueChange={setCampaignsTab} className="w-full">
              <div className="border-b border-border/40 pb-px mb-6">
                <TabsList className="bg-transparent p-0 h-auto gap-6">
                  <TabsTrigger value="ongoing" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none bg-transparent px-0 py-2 hover:text-primary/80 transition-colors">Ongoing</TabsTrigger>
                  <TabsTrigger value="paused" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none bg-transparent px-0 py-2 hover:text-primary/80 transition-colors">Paused</TabsTrigger>
                  <TabsTrigger value="completed" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none bg-transparent px-0 py-2 hover:text-primary/80 transition-colors">Completed</TabsTrigger>
                  <TabsTrigger value="archived" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none bg-transparent px-0 py-2 hover:text-primary/80 transition-colors">Archived</TabsTrigger>
                  <TabsTrigger value="deleted" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:text-primary data-[state=active]:shadow-none bg-transparent px-0 py-2 hover:text-primary/80 transition-colors">Deleted</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="ongoing" className="mt-0 space-y-4">
                <div className="grid gap-4">
                  {/* Pending Managed Requests styled as cards */}
                  {pendingManagedRequests?.map((request: any) => {
                    const isInProgress = request.status === "in_progress";
                    const targetTime = new Date(new Date(request.created_at).getTime() + 24 * 60 * 60 * 1000);

                    return (
                      <div
                        key={request.id}
                        className="group relative bg-card hover:bg-muted/30 border border-sidebar-border/60 rounded-xl p-5 transition-all duration-200 cursor-pointer hover:shadow-md"
                      >
                        <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                          {/* Left: Info */}
                          <div className="flex items-center gap-4 lg:w-[350px] shrink-0">
                            <Avatar className="h-10 w-10 border border-border/50 shrink-0">
                              <AvatarImage src={request.clients?.profile_picture_url || undefined} alt={request.clients?.linkedin_name || "Client"} />
                              <AvatarFallback className="bg-primary/10 text-primary font-medium">{request.clients?.linkedin_name?.[0] || "?"}</AvatarFallback>
                            </Avatar>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-base truncate text-foreground group-hover:text-primary transition-colors">
                                  {request.campaign_name}
                                </h3>
                                <Badge variant="outline" className={cn(
                                  "text-[10px] px-1.5 py-0 h-5 gap-1 shrink-0",
                                  isInProgress
                                    ? "border-blue-500/50 text-blue-600 bg-blue-500/5"
                                    : "border-rose-500/50 text-rose-600 bg-rose-500/5"
                                )}>
                                  {isInProgress ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />}
                                  {isInProgress ? "In Progress" : "Setting Up"}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-sm text-muted-foreground truncate">
                                  {request.clients?.linkedin_name || request.clients?.email || "Unknown Account"}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Middle: Countdown Timer */}
                          <div className="flex-1 flex flex-col items-center justify-center px-4 border-l-0 lg:border-l border-border/40 lg:px-6">
                            <div className="flex flex-col items-center">
                              <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Estimated Completion</span>
                              <CountdownTimer targetDate={targetTime} />
                            </div>
                          </div>

                          {/* Right: Actions */}
                          <div className="flex items-center justify-end gap-3 lg:pl-6 lg:border-l border-border/40 pt-4 lg:pt-0 mt-4 lg:mt-0" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-9 w-9 rounded-full hover:bg-muted text-muted-foreground hover:text-destructive"
                              onClick={() => setPendingRequestToDelete({ id: request.id, campaignId: request.campaign_id })}
                            >
                              <Trash2 className="w-5 h-5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Show regular list, but hide empty state if we have pending requests to avoid confusion */}
                {(pendingManagedRequests && pendingManagedRequests.length > 0 && ongoingCampaigns.length === 0)
                  ? null
                  : renderCampaignList(ongoingCampaigns)
                }
              </TabsContent>
              <TabsContent value="paused" className="mt-0 space-y-4">
                <div className="grid gap-4">
                  {renderCampaignList(pausedCampaigns)}
                </div>
              </TabsContent>
              <TabsContent value="completed" className="mt-0 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                {renderCampaignList(completedCampaigns)}
              </TabsContent>
              <TabsContent value="archived" className="mt-0 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                {renderCampaignList(archivedCampaigns)}
              </TabsContent>
              <TabsContent value="deleted" className="mt-0 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                {renderDeletedCampaignList()}
              </TabsContent>
            </Tabs>
            </>
          ) : (
            // Only show empty state if NO relevant items exist at all
            (!pendingManagedRequests || pendingManagedRequests.length === 0) && renderCampaignList([])
          )}
        </div>
      )}

      {/* Archive Dialog */}
      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Campaign</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to archive this campaign? This will immediately pause all activity.
              You can view it in the Archived tab and restore it anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => campaignToArchive && archiveCampaignMutation.mutate(campaignToArchive)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{campaigns?.find(c => c.id === campaignToDelete)?.name || 'Campaign'}"</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Are you sure you want to delete <strong>{campaigns?.find(c => c.id === campaignToDelete)?.name || 'this campaign'}</strong>? This action will move it to the Deleted tab where it can be restored within 30 days.</p>
              <p className="text-sm font-medium">Type the campaign name to confirm:</p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Tip:</strong> Consider archiving instead to preserve all data while pausing the campaign.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Input
              placeholder={`Enter "${campaigns?.find(c => c.id === campaignToDelete)?.name || 'campaign name'}"`}
              value={campaignNameToDelete}
              onChange={(e) => setCampaignNameToDelete(e.target.value)}
              className="w-full"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCampaignNameToDelete("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => campaignToDelete && deleteCampaignMutation.mutate(campaignToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={campaignNameToDelete !== campaigns?.find(c => c.id === campaignToDelete)?.name}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent Delete Dialog */}
      <AlertDialog open={permanentDeleteDialogOpen} onOpenChange={setPermanentDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently Delete Campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the campaign and all associated data including sequences, targets, and stats.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => campaignToPermanentlyDelete && permanentlyDeleteCampaignMutation.mutate(campaignToPermanentlyDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Pending Request Dialog */}
      <AlertDialog open={!!pendingRequestToDelete} onOpenChange={(open) => !open && setPendingRequestToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Campaign Request?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this campaign request and any associated draft campaign. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePendingRequestMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingRequestToDelete && deletePendingRequestMutation.mutate({
                requestId: pendingRequestToDelete.id,
                campaignId: pendingRequestToDelete.campaignId
              })}
              disabled={deletePendingRequestMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletePendingRequestMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Duplicate Options Dialog */}
      <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <DialogContent className="sm:max-w-xl gap-6">
          <DialogHeader className="space-y-3 pb-2 border-b border-border/40">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-primary/10 rounded-full">
                <Copy className="w-5 h-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl">Duplicate Campaign</DialogTitle>
                <DialogDescription className="text-base mt-1">
                  Choose how you want to copy this campaign
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="grid gap-3">
            {/* Option 1: Clone excluding messaged */}
            <button
              className="group flex items-center gap-4 p-4 rounded-xl border border-border/50 bg-card hover:bg-muted/50 hover:border-blue-500/30 transition-all duration-200 text-left shadow-sm hover:shadow-md"
              onClick={() => campaignToDuplicate && duplicateCampaignMutation.mutate({ campaignId: campaignToDuplicate, includeTargets: true, excludeMessaged: true })}
            >
              <div className="p-3 bg-blue-500/10 text-blue-600 rounded-lg group-hover:scale-105 transition-transform duration-200 shrink-0">
                <Filter className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-semibold text-base text-foreground group-hover:text-blue-600 transition-colors">Retarget Uncontacted</h4>
                <p className="text-sm text-muted-foreground mt-0.5 leading-snug">
                  Create a copy with only the leads who haven't received any messages yet.
                </p>
              </div>
            </button>

            {/* Option 2: Clone Everything */}
            <button
              className="group flex items-center gap-4 p-4 rounded-xl border border-border/50 bg-card hover:bg-muted/50 hover:border-amber-500/30 transition-all duration-200 text-left shadow-sm hover:shadow-md"
              onClick={() => campaignToDuplicate && duplicateCampaignMutation.mutate({ campaignId: campaignToDuplicate, includeTargets: true, excludeMessaged: false })}
            >
              <div className="p-3 bg-amber-500/10 text-amber-600 rounded-lg group-hover:scale-105 transition-transform duration-200 shrink-0">
                <Copy className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-semibold text-base text-foreground group-hover:text-amber-600 transition-colors">Clone Everything</h4>
                <p className="text-sm text-muted-foreground mt-0.5 leading-snug">
                  Duplicate the entire campaign. All leads will start from the beginning.
                </p>
              </div>
            </button>

            {/* Option 3: Duplicate Settings Only */}
            <button
              className="group flex items-center gap-4 p-4 rounded-xl border border-border/50 bg-card hover:bg-muted/50 hover:border-primary/30 transition-all duration-200 text-left shadow-sm hover:shadow-md"
              onClick={() => campaignToDuplicate && duplicateCampaignMutation.mutate({ campaignId: campaignToDuplicate, includeTargets: false })}
            >
              <div className="p-3 bg-primary/10 text-primary rounded-lg group-hover:scale-105 transition-transform duration-200 shrink-0">
                <Files className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-semibold text-base text-foreground group-hover:text-primary transition-colors">Clone Template Only</h4>
                <p className="text-sm text-muted-foreground mt-0.5 leading-snug">
                  Copy settings and sequences only. No leads included.
                </p>
              </div>
            </button>
          </div>
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={setRenameDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rename Campaign</DialogTitle>
            <DialogDescription>
              Enter a new name for your campaign.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="campaign-name">Campaign Name</Label>
              <Input
                id="campaign-name"
                value={newCampaignName}
                onChange={(e) => setNewCampaignName(e.target.value)}
                placeholder="Enter campaign name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newCampaignName.trim() && campaignToRename) {
                    renameCampaignMutation.mutate({ id: campaignToRename.id, name: newCampaignName.trim() });
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (newCampaignName.trim() && campaignToRename) {
                  renameCampaignMutation.mutate({ id: campaignToRename.id, name: newCampaignName.trim() });
                }
              }}
              disabled={!newCampaignName.trim() || renameCampaignMutation.isPending}
            >
              {renameCampaignMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {attachTarget && workspace?.id && (
        <AttachAccountDialog
          open
          onOpenChange={(v) => !v && setAttachTarget(null)}
          campaign={attachTarget}
          workspaceId={workspace.id}
          onAttached={() => setAttachTarget(null)}
        />
      )}

      <AlertDialog open={!!reopenTarget} onOpenChange={(open) => !open && setReopenTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reopen completed campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This will move <strong>{reopenTarget?.name}</strong> back to <strong>Paused</strong>.
              Existing targets, sequence, and history are preserved. You will need to add new
              targets and press Start to continue sending.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reopening}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={reopening}
              onClick={async (e) => {
                e.preventDefault();
                if (!reopenTarget) return;
                setReopening(true);
                try {
                  const { data, error } = await supabase
                    .from("campaigns")
                    .update({
                      status: "paused",
                      paused_reason: "reopened_by_user",
                      paused_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                    })
                    .eq("id", reopenTarget.id)
                    .eq("status", "completed")
                    .select("id")
                    .maybeSingle();
                  if (error) throw error;
                  if (!data) {
                    toast.error("Campaign is no longer completed. Reloading…");
                  } else {
                    supabase.from("critical_operation_log").insert({
                      operation: "campaign_reopened",
                      campaign_id: reopenTarget.id,
                      workspace_id: workspace?.id,
                      details: { via: "user" },
                    } as any).then(() => {});
                    toast.success("Campaign reopened. Add more targets, then start it.");
                  }
                  queryClient.invalidateQueries({ queryKey: ["campaigns", workspace?.id] });
                  setReopenTarget(null);
                } catch (err: any) {
                  toast.error(err?.message || "Failed to reopen campaign");
                } finally {
                  setReopening(false);
                }
              }}
            >
              {reopening ? "Reopening..." : "Reopen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {capacityDialog}
    </div>

  );
}
